/**
 * KeyboardAware.js
 *
 * Shared keyboard-avoidance + scrolling primitives so EVERY screen and modal in
 * the app behaves the same on Android, iOS and web: content is always fully
 * scrollable and the soft keyboard never covers the focused input.
 *
 * Why two different behaviours:
 *  - Full SCREENS: the Android activity uses `windowSoftInputMode="adjustResize"`
 *    (AndroidManifest), so the window itself shrinks when the keyboard opens and
 *    a ScrollView reveals the field — Android therefore needs NO extra avoidance
 *    (behavior `undefined`), while iOS needs `padding`.
 *  - MODALS: a React-Native <Modal> renders in its OWN window which does NOT
 *    inherit the activity's adjustResize, so Android must explicitly avoid the
 *    keyboard (behavior `height`); iOS still uses `padding`.
 *
 * On web `KeyboardAvoidingView` is effectively a no-op (no soft keyboard) and the
 * ScrollView maps to a scrollable div, so the same components keep content
 * scrollable in the browser too.
 */

import React from 'react';
import {
    KeyboardAvoidingView,
    Platform,
    ScrollView,
    StyleSheet,
    View,
} from 'react-native';

/** Behaviour for a full-screen avoider (Android relies on adjustResize). */
export const screenKavBehavior = Platform.OS === 'ios' ? 'padding' : undefined;
/** Behaviour for an avoider INSIDE a RN <Modal> (no adjustResize there). */
export const modalKavBehavior = Platform.OS === 'ios' ? 'padding' : 'height';

/**
 * Full-screen keyboard avoider. Wrap a screen's scrollable body in this.
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {object} [props.style]
 * @param {number} [props.offset] keyboardVerticalOffset (e.g. header height).
 */
export const ScreenKeyboardAvoider = ({children, style, offset = 0}) => (
    <KeyboardAvoidingView
        style={[styles.flex, style]}
        behavior={screenKavBehavior}
        keyboardVerticalOffset={offset}
    >
        {children}
    </KeyboardAvoidingView>
);

/**
 * Drop-in scrollable, keyboard-aware screen body. Use for any screen with inputs.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {object} [props.style] outer avoider style
 * @param {object} [props.contentContainerStyle] ScrollView content style
 * @param {number} [props.offset] keyboardVerticalOffset
 * @param {boolean} [props.scrollEnabled=true]
 * @param {('never'|'always'|'handled')} [props.keyboardShouldPersistTaps='handled']
 */
export const KeyboardAwareScreen = ({
    children,
    style,
    contentContainerStyle,
    offset = 0,
    scrollEnabled = true,
    keyboardShouldPersistTaps = 'handled',
    ...rest
}) => (
    <ScreenKeyboardAvoider style={style} offset={offset}>
        <ScrollView
            style={styles.flex}
            contentContainerStyle={[styles.grow, contentContainerStyle]}
            keyboardShouldPersistTaps={keyboardShouldPersistTaps}
            keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
            showsVerticalScrollIndicator={false}
            scrollEnabled={scrollEnabled}
            {...rest}
        >
            {children}
        </ScrollView>
    </ScreenKeyboardAvoider>
);

/**
 * Keyboard avoider for content INSIDE a RN <Modal>. Wrap the modal overlay's
 * inner content (sheet/card) in this so the keyboard pushes it up on Android too.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {object} [props.style]
 * @param {('flex-end'|'center'|'flex-start')} [props.justify='flex-end'] vertical
 *   alignment of the modal content within the avoider.
 * @param {number} [props.offset] keyboardVerticalOffset
 * @param {boolean} [props.pointerEventsBoxNone] let scrim taps through empty area.
 */
export const ModalKeyboardAvoider = ({
    children,
    style,
    justify = 'flex-end',
    offset = 0,
    pointerEventsBoxNone = false,
}) => (
    <KeyboardAvoidingView
        style={[styles.flex, {justifyContent: justify}, style]}
        behavior={modalKavBehavior}
        keyboardVerticalOffset={offset}
        pointerEvents={pointerEventsBoxNone ? 'box-none' : 'auto'}
    >
        {children}
    </KeyboardAvoidingView>
);

/**
 * Scrollable body for a modal card/sheet so tall forms never clip and the
 * keyboard can scroll fields into view. Use inside a modal card.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {object} [props.style]
 * @param {object} [props.contentContainerStyle]
 */
export const ModalScrollBody = ({children, style, contentContainerStyle, ...rest}) => (
    <ScrollView
        style={style}
        contentContainerStyle={contentContainerStyle}
        keyboardShouldPersistTaps="handled"
        keyboardDismissMode={Platform.OS === 'ios' ? 'interactive' : 'on-drag'}
        showsVerticalScrollIndicator={false}
        bounces={false}
        {...rest}
    >
        {children}
    </ScrollView>
);

const styles = StyleSheet.create({
    flex: {flex: 1},
    grow: {flexGrow: 1},
    // eslint-disable-next-line react-native/no-unused-styles
    fill: {...StyleSheet.absoluteFillObject},
});

export default {
    KeyboardAwareScreen,
    ScreenKeyboardAvoider,
    ModalKeyboardAvoider,
    ModalScrollBody,
    screenKavBehavior,
    modalKavBehavior,
};

// `View` re-exported intentionally for callers that compose their own layout.
export {View};
